import { computed, inject, Injectable } from '@angular/core';
import { UserRole } from '@klubovna/api-client';
import { Store } from '@ngrx/store';
import { UserFacade } from '@store/user/user.facade';
import * as UsersActions from './users.actions';
import { CreateUserData, UpdateUserData, UsersQueryParams } from './users.actions';
import * as UsersSelectors from './users.selectors';

const ROLE_HIERARCHY_LEVEL: Record<UserRole, number> = {
  [UserRole.Member]: 1,
  [UserRole.Volunteer]: 2,
  [UserRole.Cashier]: 2,
  [UserRole.EventAdmin]: 2,
  [UserRole.Admin]: 3,
  [UserRole.Owner]: 4,
};

@Injectable({
  providedIn: 'root',
})
export class UsersFacade {
  private store = inject(Store);
  private userFacade = inject(UserFacade);

  users$ = this.store.select(UsersSelectors.selectAllUsers);
  loading$ = this.store.select(UsersSelectors.selectUsersLoading);
  error$ = this.store.select(UsersSelectors.selectUsersError);
  selectedUser$ = this.store.select(UsersSelectors.selectSelectedUser);
  pagination$ = this.store.select(UsersSelectors.selectUsersPagination);

  availableRoles = computed(() => {
    const currentUser = this.userFacade.user();
    if (!currentUser?.role) return [];
    return this.getAvailableRoles(currentUser.role);
  });

  loadUsers(params?: UsersQueryParams): void {
    this.store.dispatch(UsersActions.loadUsers({ params }));
  }

  createUser(userData: CreateUserData): void {
    this.store.dispatch(UsersActions.createUser({ userData }));
  }

  updateUser(id: string, userData: UpdateUserData): void {
    this.store.dispatch(UsersActions.updateUser({ id, userData }));
  }

  deleteUser(id: string): void {
    this.store.dispatch(UsersActions.deleteUser({ id }));
  }

  selectUser(user: any): void {
    this.store.dispatch(UsersActions.selectUser({ user }));
  }

  canCreateRole(currentUserRole: UserRole, targetRole: UserRole): boolean {
    const availableRoles = this.getAvailableRoles(currentUserRole);
    return availableRoles.includes(targetRole);
  }

  canEditUser(currentUserRole: UserRole, targetUserRole: UserRole): boolean {
    return this.isRoleStrictlyAbove(currentUserRole, targetUserRole);
  }

  canDeleteUser(currentUserRole: UserRole, targetUserRole: UserRole): boolean {
    return this.isRoleStrictlyAbove(currentUserRole, targetUserRole);
  }

  setUserActive(id: string, isActive: boolean): void {
    this.updateUser(id, { isActive });
  }

  private isRoleStrictlyAbove(actorRole: UserRole, targetRole: UserRole): boolean {
    return ROLE_HIERARCHY_LEVEL[actorRole] > ROLE_HIERARCHY_LEVEL[targetRole];
  }

  private getAvailableRoles(currentUserRole: UserRole): UserRole[] {
    switch (currentUserRole) {
      case UserRole.Owner:
        return [ UserRole.Admin, UserRole.EventAdmin, UserRole.Volunteer ];
      case UserRole.Admin:
        return [ UserRole.EventAdmin, UserRole.Volunteer ];
      default:
        return [];
    }
  }

  getRoleDisplayName(role: UserRole): string {
    switch (role) {
      case UserRole.Owner:
        return 'Owner';
      case UserRole.Admin:
        return 'Admin';
      case UserRole.EventAdmin:
        return 'Event Admin';
      case UserRole.Volunteer:
        return 'Volunteer';
      case UserRole.Member:
        return 'Member';
      default:
        return role;
    }
  }
}
